import { FormEvent, FunctionComponent, KeyboardEvent, useCallback, useRef, useState } from 'react'
import { IconButton, TextArea } from '../../shared'
import { ChatComposerProps } from './ChatComposer.model'
import styles from './ChatComposer.module.scss'

export const ChatComposer: FunctionComponent<ChatComposerProps> = ({
  disabled = false,
  isProcessing = false,
  isEditing = false,
  initialValue = '',
  placeholder = 'Posez une question sur vos statistiques…',
  onDraftChange,
  onSend,
  onStop,
  onCancelEdit,
}) => {
  const [value, setValue] = useState(initialValue)
  const valueRef = useRef(value)
  valueRef.current = value

  const submit = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed || disabled || isProcessing) return
    const result = onSend(trimmed)
    setValue('')
    onDraftChange?.('')

    // Envoi échoué (ex. création de conversation refusée) : on rend la question,
    // sauf si l'utilisateur a recommencé à écrire entre-temps.
    void Promise.resolve(result).then((sent) => {
      if (sent !== false || valueRef.current) return
      setValue(trimmed)
      onDraftChange?.(trimmed)
    })
  }, [disabled, isProcessing, onDraftChange, onSend, value])

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault()
      submit()
    },
    [submit],
  )

  const handleKeyPress = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        submit()
      }
    },
    [submit],
  )

  // TextArea n'expose que onKeyPress, qui ne reçoit pas Échap.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLFormElement>) => {
      if (event.key === 'Escape' && isEditing) onCancelEdit?.()
    },
    [isEditing, onCancelEdit],
  )

  return (
    <form className={styles.composer} onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
      {isEditing && <div className={styles.editBanner}>Modification du message</div>}
      <div className={styles.field}>
        <div className={styles.textarea}>
          <TextArea
            name="stats_assistant_question"
            value={value}
            initialRows={1}
            autoResize
            borderless
            resize="none"
            disabled={disabled || isProcessing}
            placeholder={isEditing ? 'Modifiez votre question…' : placeholder}
            onChange={(event) => {
              setValue(event.target.value)
              onDraftChange?.(event.target.value)
            }}
            onKeyPress={handleKeyPress}
            testId="textarea-stats_assistant-question"
          />
        </div>
        {isEditing && (
          <IconButton
            icon="cross"
            theme="transparent-dark"
            size="micro"
            title="Annuler la modification"
            onClick={onCancelEdit}
            testId="button-stats_assistant-cancel_edit"
          />
        )}
        {isProcessing ? (
          <IconButton
            icon="stop"
            theme="transparent-dark"
            size="micro"
            title="Arrêter l’analyse"
            onClick={onStop}
            testId="button-stats_assistant-stop"
          />
        ) : (
          <IconButton
            icon="arrow"
            rotate={90}
            theme="primary"
            size="micro"
            type="submit"
            title="Envoyer"
            disabled={disabled || !value.trim()}
            testId="button-stats_assistant-send"
          />
        )}
      </div>
    </form>
  )
}
